import { useState, useEffect, useRef } from 'react';
import { trackContentEvent } from '@/analytics/analytics';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Download, Search, Loader2, Play, FileText, ChevronDown, ChevronUp, Clock, BookOpen, GraduationCap, School, Sparkles, Headphones } from 'lucide-react';
import BudleeAIIcon from '@/components/BudleeAIIcon';
import { curiosityAPI } from '../services/curiosityAPI';
import BudleeAIChatPanel from '@/components/BudleeAIChatPanel';
import { useSubjectChapter } from '@/context/SubjectChapterContext';
import { usePoints } from '@/context/PointsContext';
import FloatingPointsAnimation, { useFloatingPointsAnimation } from '@/components/FloatingPointsAnimation';
import StatsBar from '@/components/StatsBar';

// Mock transcript data generator
const generateMockTranscript = () => {
  const segments = [
    { start: 0, text: 'Welcome to this educational video. Today we will explore fundamental concepts.' },
    { start: 5, text: 'First, let us understand the basic principles that govern this topic.' },
    { start: 12, text: 'These principles are essential for building a strong foundation.' },
    { start: 20, text: 'Now, let\'s look at some practical examples to illustrate these concepts.' },
    { start: 28, text: 'Example one demonstrates how these principles work in real-world scenarios.' },
    { start: 35, text: 'As you can see, the application is straightforward and intuitive.' },
    { start: 42, text: 'Moving on to example two, we see a more complex application.' },
    { start: 50, text: 'This example requires a deeper understanding of the underlying mechanisms.' },
    { start: 58, text: 'Let\'s break down each component step by step.' },
    { start: 65, text: 'The first component is crucial for understanding the overall system.' },
    { start: 72, text: 'The second component builds upon the first, creating a layered approach.' },
    { start: 80, text: 'Finally, the third component integrates everything together.' },
    { start: 88, text: 'Now that we\'ve covered the basics, let\'s explore advanced topics.' },
    { start: 95, text: 'Advanced concepts require careful consideration and practice.' },
    { start: 103, text: 'Remember to practice regularly to master these skills.' },
    { start: 110, text: 'Thank you for watching. Don\'t forget to check out the related resources.' },
  ];

  return segments.map((seg, index) => ({
    ...seg,
    duration: index < segments.length - 1 ? segments[index + 1].start - seg.start : 5,
  }));
};

/**
 * Video Player with Transcript
 * Path: /curiosity/video/{contentId}
 * 
 * Features:
 * - YouTube iframe embed (use YouTube iframe API)
 * - Transcript panel (side panel or bottom panel, collapsible)
 * - Transcript with timestamps (clickable)
 * - Click timestamp → Video jumps to that time
 * - Search within transcript
 * - Auto-scroll transcript as video plays (sync with current time)
 * - Download transcript button (plain text file)
 * - Track video completion percentage
 */
export default function VideoPlayer() {
  const { contentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [transcriptText, setTranscriptText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentTime, setCurrentTime] = useState(0);
  const [player, setPlayer] = useState(null);
  const [transcriptPanelOpen, setTranscriptPanelOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [showChatPanel, setShowChatPanel] = useState(false);
  const [prefilledText, setPrefilledText] = useState('');
  const [isWatched, setIsWatched] = useState(false);
  const playerRef = useRef(null);
  const transcriptRef = useRef(null);
  const timeTrackingIntervalRef = useRef(null);
  const prevTimeRef = useRef(0);
  const gaSecondsCounterRef = useRef(0);
  const ytApiReadyRef = useRef(false);
  const { selectedSubjectId, selectedChapterId } = useSubjectChapter();
  const { refreshPoints } = usePoints();
  const { addAnimation, renderAnimations } = useFloatingPointsAnimation();
  const videoPlayerRef = useRef(null);
  const statsBarRef = useRef(null);

  const isSmallScreen = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 767px)').matches;

  useEffect(() => {
    if (contentId) {
      loadContent();
    }
  }, [contentId]);

  // Load YouTube IFrame API
  useEffect(() => {
    if (window.YT && window.YT.Player) {
      ytApiReadyRef.current = true;
      if (content?.youtube_video_id) {
        initializePlayer();
      }
      return;
    }

    // Check if script is already being loaded
    if (document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      // Script is loading, wait for it
      if (!window.onYouTubeIframeAPIReady) {
        window.onYouTubeIframeAPIReady = () => {
          ytApiReadyRef.current = true;
          if (content?.youtube_video_id) {
            initializePlayer();
          }
        };
      }
      return;
    }

    // Load the script
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    window.onYouTubeIframeAPIReady = () => {
      ytApiReadyRef.current = true;
      if (content?.youtube_video_id) {
        initializePlayer();
      }
    };

    return () => {
      if (player) {
        try {
          player.destroy();
        } catch (e) {
          console.error('Error destroying player:', e);
        }
      }
      if (timeTrackingIntervalRef.current) {
        clearInterval(timeTrackingIntervalRef.current);
      }
    };
  }, []);

  // Initialize player when content and API are ready
  useEffect(() => {
    if (content?.youtube_video_id && ytApiReadyRef.current && !player) {
      initializePlayer();
    }
  }, [content, ytApiReadyRef.current]);

  const loadContent = async () => {
    try {
      setLoading(true);

      const contentData = await curiosityAPI.getContent(contentId);
      setContent(contentData);

      // Check if content is already watched (from API response or fallback to separate call)
      if (contentData.is_watched !== undefined) {
        setIsWatched(contentData.is_watched);
      } else {
        // Fallback: check via interactions API if is_watched not in response
        try {
          const interactions = await curiosityAPI.getInteractions({ content: parseInt(contentId) });
          const hasCompleted = interactions.some(interaction => interaction.interaction_type === 'completed');
          setIsWatched(hasCompleted);
        } catch (err) {
          console.warn('Failed to check watched status:', err);
          setIsWatched(false);
        }
      }

      // Load transcript if available - prioritize transcript_text
      if (contentData.transcript) {
        setTranscript(contentData.transcript);
        setTranscriptText(contentData.transcript.transcript_text || '');
      } else {
        // Try to get transcript separately if not included in content
        try {
          const transcriptData = await curiosityAPI.getTranscript(contentId);
          if (transcriptData) {
            setTranscript(transcriptData);
            setTranscriptText(transcriptData.transcript_text || '');
          } else {
            setTranscript(null);
            setTranscriptText('');
          }
        } catch (err) {
          console.warn('Failed to load transcript separately:', err);
          setTranscript(null);
          setTranscriptText('');
        }
      }

      // Track "viewed" interaction (non-blocking)
      curiosityAPI.recordInteraction({
        content: parseInt(contentId),
        interaction_type: 'viewed',
      }).catch(err => console.warn('Failed to record interaction:', err));
    } catch (error) {
      console.error('Failed to load content:', error);
    } finally {
      setLoading(false);
    }
  };

  const initializePlayer = () => {
    if (!content?.youtube_video_id || !playerRef.current || !window.YT || !window.YT.Player) {
      return;
    }

    // Destroy existing player if any
    if (player) {
      try {
        player.destroy();
      } catch (e) {
        console.error('Error destroying existing player:', e);
      }
    }

    try {
      const ytPlayer = new window.YT.Player(playerRef.current, {
        videoId: content.youtube_video_id,
        playerVars: {
          autoplay: 0,
          controls: 1,
          rel: 0,
          modestbranding: 1,
          enablejsapi: 1,
        },
        events: {
          onReady: (event) => {
            const playerInstance = event.target;
            setPlayer(playerInstance);

            // Get video duration
            try {
              const duration = playerInstance.getDuration();
              setVideoDuration(duration);
            } catch (e) {
              console.error('Could not get video duration:', e);
            }
          },
          onStateChange: (event) => {
            if (event.data === window.YT.PlayerState.PLAYING) {
              setIsPlaying(true);
              startTimeTracking();
              try {
                trackContentEvent('content_play', {
                  content_id: contentId ? parseInt(contentId) : null,
                  content_type: 'video',
                  content_title: content?.title || null,
                  duration: videoDuration || null,
                  subject_id: content?.chapter_detail?.subject_id || selectedSubjectId || null,
                  chapter_id: content?.chapter_detail?.chapter_id || selectedChapterId || null,
                });
              } catch (e) {}
            } else if (event.data === window.YT.PlayerState.PAUSED) {
              setIsPlaying(false);
              stopTimeTracking();
                try {
                  trackContentEvent('content_pause', {
                    content_id: contentId ? parseInt(contentId) : null,
                    content_type: 'video',
                    content_title: content?.title || null,
                    subject_id: content?.chapter_detail?.subject_id || selectedSubjectId || null,
                    chapter_id: content?.chapter_detail?.chapter_id || selectedChapterId || null,
                  });
                } catch (e) {}
            } else if (event.data === window.YT.PlayerState.ENDED) {
              setIsPlaying(false);
              stopTimeTracking();

              // Only track completion and award XP if not already watched
              if (!isWatched) {
                // Track completion (non-blocking)
                curiosityAPI.recordInteraction({
                  content: parseInt(contentId),
                  interaction_type: 'completed',
                  completion_percentage: 100,
                }).then(async () => {
                  setIsWatched(true);
                  // Refresh points after recording completion (backend signal will award XP)
                  await refreshPoints();

                  // Trigger floating animation for XP earned (more noticeable in center)
                  setTimeout(() => {
                    const targetPosition = statsBarRef.current
                      ? (() => {
                        const rect = statsBarRef.current.getBoundingClientRect();
                        return {
                          x: rect.left + rect.width / 2,
                          y: rect.top + rect.height / 2,
                        };
                      })()
                      : { x: window.innerWidth - 100, y: 60 };

                    // Start from center of video player for more visibility
                    const startPosition = {
                      x: window.innerWidth / 2,
                      y: window.innerHeight / 2,
                    };

                    // Determine XP amount based on tag_type
                    const xpAmount = content?.tag_type === 'core' ? 5 : content?.tag_type === 'deep' ? 8 : 3;
                    addAnimation('xp', xpAmount, startPosition, targetPosition);
                  }, 50);
                }).catch(err => console.warn('Failed to record completion:', err));
              }

                try {
                  trackContentEvent('content_complete', {
                    content_id: contentId ? parseInt(contentId) : null,
                    content_type: 'video',
                    content_title: content?.title || null,
                    completion_percentage: 100,
                  });
                } catch (e) {}
            }
          },
          onError: (event) => {
            console.error('YouTube player error:', event.data);
          },
        },
      });
    } catch (error) {
      console.error('Error initializing YouTube player:', error);
    }
  };

  const startTimeTracking = () => {
    // Clear any existing interval
    if (timeTrackingIntervalRef.current) {
      clearInterval(timeTrackingIntervalRef.current);
    }

    timeTrackingIntervalRef.current = setInterval(() => {
      if (player) {
        try {
          const time = player.getCurrentTime();
          setCurrentTime(time);

          // Seek detection (large jumps)
          try {
            const prev = prevTimeRef.current || 0;
            if (Math.abs(time - prev) > 3 && Math.abs(time - prev) > 1) {
              // Treat as seek
              trackContentEvent('content_seek', {
                content_id: contentId ? parseInt(contentId) : null,
                content_type: 'video',
                from_seconds: prev,
                to_seconds: time,
              });
            }
          } catch (e) {}
          prevTimeRef.current = time;

          // Update completion percentage
          if (videoDuration > 0) {
            const percentage = Math.min((time / videoDuration) * 100, 100);
            setCompletionPercentage(percentage);
          }

          // Periodic GA time_spent every ~10s
          gaSecondsCounterRef.current += 0.5; // interval is 500ms
          if (gaSecondsCounterRef.current >= 10) {
            try {
              trackContentEvent('content_time_spent', {
                content_id: contentId ? parseInt(contentId) : null,
                content_type: 'video',
                    time_spent_seconds: Math.round(gaSecondsCounterRef.current),
                    completion_percentage: Math.round(completionPercentage),
                    content_title: content?.title || null,
                    subject_id: content?.chapter_detail?.subject_id || selectedSubjectId || null,
                    chapter_id: content?.chapter_detail?.chapter_id || selectedChapterId || null,
              });
            } catch (e) {}
            gaSecondsCounterRef.current = 0;
          }
        } catch (e) {
          console.error('Error getting current time:', e);
          stopTimeTracking();
        }
      }
    }, 500); // Update every 500ms for smoother tracking
  };

  const stopTimeTracking = () => {
    if (timeTrackingIntervalRef.current) {
      clearInterval(timeTrackingIntervalRef.current);
      timeTrackingIntervalRef.current = null;
    }
  };

  const handleDownloadTranscript = () => {
    if (!transcriptText) return;

    const blob = new Blob([transcriptText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript-${content?.title || 'video'}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleAskBudleeAI = () => {
    // Set transcript as prefilled text for RAG context
    if (transcriptText) {
      setPrefilledText(transcriptText);
    }
    setShowChatPanel(true);
  };

  const handleCloseChatPanel = () => {
    setShowChatPanel(false);
    setPrefilledText('');
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] overflow-y-auto">
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--green-bright))]" />
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] overflow-y-auto">
        <div className="max-w-4xl mx-auto w-full px-6 py-12">
          <div className="text-center space-y-4">
            <p className="text-muted-foreground text-lg">Content not found</p>
            <Button
              onClick={() => navigate(-1)}
              className="bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))]"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Search in transcript text
  const filteredTranscriptText = transcriptText
    ? (searchQuery
      ? transcriptText
        .split('\n')
        .filter(line => line.toLowerCase().includes(searchQuery.toLowerCase()))
        .join('\n')
      : transcriptText)
    : '';

  return (
    <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] overflow-hidden relative">
      {/* Mobile StatsBar - Show at top on mobile only */}
      <div className="flex-shrink-0 pt-2 pb-2 flex justify-center lg:hidden">
        <div className="w-fit">
          <StatsBar />
        </div>
      </div>

      {/* Main Content Container */}
      <div className="flex flex-1 h-full bg-[hsl(var(--main-bg))] overflow-hidden relative">
        {/* StatsBar - Absolutely positioned top right (desktop only) - Hide when chat panel is open (BudleeAIChatPanel already shows stats) */}
        {!showChatPanel && !isSmallScreen && (
          <div className="absolute top-4 right-8 z-40" ref={statsBarRef}>
            <StatsBar />
          </div>
        )}

      {/* Floating Animations */}
      {renderAnimations()}

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col overflow-y-auto transition-all duration-300 ${!isSmallScreen && showChatPanel ? 'pr-[400px]' : ''} ${isSmallScreen && showChatPanel ? 'h-1/2' : ''}`}>
        <div className="max-w-7xl mx-auto w-full px-6 py-6 space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/student/curiosity')}
              className="text-foreground hover:bg-[hsl(var(--card-bg))] hover:text-foreground"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Curiosity Centre
            </Button>
          </div>

          {/* Video Player - Shown First */}
          <div className="space-y-4">
            <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] overflow-hidden">
              <CardContent className="p-0">
                <div className="aspect-video bg-black relative">
                  {content.youtube_video_id ? (
                    <div ref={(el) => { playerRef.current = el; videoPlayerRef.current = el?.parentElement || el; }} className="w-full h-full"></div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      <div className="text-center">
                        <Play className="h-16 w-16 mx-auto mb-4 opacity-50" />
                        <p>Video player loading...</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Title and Description - Matching Screenshot Style */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              {content.tag_type && (
                <Badge
                  variant={content.tag_type === 'core' ? 'default' : 'secondary'}
                  className="px-3 py-1"
                >
                  {content.tag_type === 'core' ? 'CORE' : 'GO DEEPER'}
                </Badge>
              )}
              <Badge variant="outline" className="px-3 py-1">
                Video
              </Badge>
              {isWatched && (
                <Badge variant="default" className="px-3 py-1 bg-green-500/20 text-green-400 border-green-500/50">
                  Watched
                </Badge>
              )}
              {content.video_duration_formatted && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm">{content.video_duration_formatted}</span>
                </div>
              )}
            </div>
            <h1 className="text-xl sm:text-2xl lg:text-4xl font-bold text-foreground">{content.title}</h1>
            {content.description && (
              <p className="text-muted-foreground text-xs sm:text-sm lg:text-lg max-w-3xl">{content.description}</p>
            )}
          </div>

          {/* Mobile: Quick Actions - Show above Transcript on mobile only */}
          {isSmallScreen && (
            <div className="space-y-4">
              <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                <CardContent className="p-4">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h2>
                  <div className="space-y-3">
                    <Button
                      className="w-full bg-gradient-to-r from-red-500 to-rose-600 hover:opacity-90 text-white font-semibold"
                      onClick={() => {
                        if (player) {
                          player.playVideo();
                        }
                      }}
                    >
                      <Play className="mr-2 h-4 w-4" />
                      Watch Video
                    </Button>
                    <Button
                      className="w-full bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-semibold"
                      onClick={handleAskBudleeAI}
                    >
                      <Sparkles className="mr-2 h-3 w-3" />
                      Talk to Budlee
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <div className="space-y-4">
            {/* Transcript Panel - Below Video */}
            <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
              <CardContent className="p-4">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-2">
                    <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4 lg:h-5 lg:w-5 text-[hsl(var(--teal-vivid))]" />
                    <h2 className="text-sm sm:text-base lg:text-lg font-semibold text-foreground">Transcript</h2>
                  </div>
                  <div className="flex gap-2">
                    {transcriptText && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleDownloadTranscript}
                        title="Download transcript"
                        className="hover:bg-[hsl(var(--card-bg))]"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setTranscriptPanelOpen(!transcriptPanelOpen)}
                      className="hover:bg-[hsl(var(--card-bg))]"
                    >
                      {transcriptPanelOpen ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {transcriptPanelOpen && (
                  <div className="space-y-4">
                    {/* Search */}
                    {transcriptText && (
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search transcript..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-8 bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))]"
                        />
                      </div>
                    )}

                    {/* Transcript Content */}
                    <div
                      ref={transcriptRef}
                      className="max-h-[400px] overflow-y-auto space-y-2 text-sm scrollbar-thin"
                    >
                      {transcriptText ? (
                        searchQuery ? (
                          filteredTranscriptText ? (
                            <p className="text-foreground whitespace-pre-wrap">{filteredTranscriptText}</p>
                          ) : (
                            <p className="text-muted-foreground text-center py-4">No matching transcript segments found for "{searchQuery}".</p>
                          )
                        ) : (
                          <p className="text-foreground whitespace-pre-wrap">{transcriptText}</p>
                        )
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                          <p>No transcript available</p>
                          {content.content_type === 'video' && (
                            <p className="text-sm mt-2">Transcript generation may be in progress</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Mobile: Content Info and Related Content - Show below Transcript on mobile only */}
          {isSmallScreen && (
            <div className="space-y-4">
              {/* Content Info Card */}
              <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                <CardContent className="p-4">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Content Info</h2>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Type:</span>
                      <span className="ml-2 text-foreground font-medium">Video</span>
                    </div>
                    {content.tag_type && (
                      <div>
                        <span className="text-muted-foreground">Category:</span>
                        <span className="ml-2 text-foreground font-medium">
                          {content.tag_type === 'core' ? 'CORE' : 'GO DEEPER'}
                        </span>
                      </div>
                    )}
                    {content.chapter_detail?.board_name && (
                      <div>
                        <span className="text-muted-foreground">Board:</span>
                        <span className="ml-2 text-foreground font-medium">{content.chapter_detail.board_name}</span>
                      </div>
                    )}
                    {content.chapter_detail?.grade_name && (
                      <div>
                        <span className="text-muted-foreground">Grade:</span>
                        <span className="ml-2 text-foreground font-medium">{content.chapter_detail.grade_name}</span>
                      </div>
                    )}
                    {content.chapter_detail?.subject_name && (
                      <div>
                        <span className="text-muted-foreground">Subject:</span>
                        <span className="ml-2 text-foreground font-medium">{content.chapter_detail.subject_name}</span>
                      </div>
                    )}
                    {content.chapter_detail?.chapter_name && (
                      <div>
                        <span className="text-muted-foreground">Chapter:</span>
                        <span className="ml-2 text-foreground font-medium">{content.chapter_detail.chapter_name}</span>
                      </div>
                    )}
                    {content.video_duration_formatted && (
                      <div>
                        <span className="text-muted-foreground">Duration:</span>
                        <span className="ml-2 text-foreground font-medium">{content.video_duration_formatted}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Related Content Card */}
              <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                <CardContent className="p-4">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Related Content</h2>
                  <div className="text-sm text-muted-foreground">
                    <p>Related content will be displayed here</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Right Sidebar - Quick Actions (shown when chat panel is closed) */}
      {!showChatPanel && !isSmallScreen && (
        <div className="w-96 bg-[hsl(var(--sidebar-bg))] border-l border-[hsl(var(--card-border))] flex flex-col overflow-y-auto">
          <div className="p-6 space-y-4 pt-24">
            {/* Quick Actions Card */}
            <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
              <CardContent className="p-4">
                <h2 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h2>
                <div className="space-y-3">
                  <Button
                    className="w-full bg-gradient-to-r from-red-500 to-rose-600 hover:opacity-90 text-white font-semibold"
                    onClick={() => {
                      if (player) {
                        player.playVideo();
                      }
                    }}
                  >
                    <Play className="mr-2 h-4 w-4" />
                    Watch Video
                  </Button>
                  <Button
                    className="w-full bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-semibold"
                    onClick={handleAskBudleeAI}
                  >
                    <Sparkles className="mr-2 h-3 w-3" />
                    Talk to Budlee
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Content Info Card */}
            <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
              <CardContent className="p-4">
                <h2 className="text-lg font-semibold text-foreground mb-4">Content Info</h2>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Type:</span>
                    <span className="ml-2 text-foreground font-medium">Video</span>
                  </div>
                  {content.tag_type && (
                    <div>
                      <span className="text-muted-foreground">Category:</span>
                      <span className="ml-2 text-foreground font-medium">
                        {content.tag_type === 'core' ? 'CORE' : 'GO DEEPER'}
                      </span>
                    </div>
                  )}
                  {content.chapter_detail?.board_name && (
                    <div>
                      <span className="text-muted-foreground">Board:</span>
                      <span className="ml-2 text-foreground font-medium">{content.chapter_detail.board_name}</span>
                    </div>
                  )}
                  {content.chapter_detail?.grade_name && (
                    <div>
                      <span className="text-muted-foreground">Grade:</span>
                      <span className="ml-2 text-foreground font-medium">{content.chapter_detail.grade_name}</span>
                    </div>
                  )}
                  {content.chapter_detail?.subject_name && (
                    <div>
                      <span className="text-muted-foreground">Subject:</span>
                      <span className="ml-2 text-foreground font-medium">{content.chapter_detail.subject_name}</span>
                    </div>
                  )}
                  {content.chapter_detail?.chapter_name && (
                    <div>
                      <span className="text-muted-foreground">Chapter:</span>
                      <span className="ml-2 text-foreground font-medium">{content.chapter_detail.chapter_name}</span>
                    </div>
                  )}
                  {content.video_duration_formatted && (
                    <div>
                      <span className="text-muted-foreground">Duration:</span>
                      <span className="ml-2 text-foreground font-medium">{content.video_duration_formatted}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Related Content Card */}
            <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
              <CardContent className="p-4">
                <h2 className="text-lg font-semibold text-foreground mb-4">Related Content</h2>
                <div className="text-sm text-muted-foreground">
                  <p>Related content will be displayed here</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Chat Panel - Right Side when Ask Budlee AI is clicked - Fixed Position like CuriosityCentre */}
      {showChatPanel && !isSmallScreen && (
        <div className="fixed right-0 top-0 h-screen w-[400px] z-50">
          <BudleeAIChatPanel
            onClose={handleCloseChatPanel}
            prefilledText={prefilledText}
            onClearPrefilledText={() => setPrefilledText('')}
            pageContext="curiosity_video"
            curiosityContentId={contentId ? parseInt(contentId) : null}
            subjectId={content?.chapter_detail?.subject_id || selectedSubjectId || null}
            chapterId={content?.chapter_detail?.chapter_id || selectedChapterId || null}
          />
        </div>
      )}

      {/* Main content div closing tag */}
      </div>

      {showChatPanel && isSmallScreen && (
        <div className="fixed inset-x-0 bottom-0 h-1/2 z-50 bg-[hsl(var(--main-bg))] border-t border-[hsl(var(--card-border))]">
          <BudleeAIChatPanel
            onClose={handleCloseChatPanel}
            prefilledText={prefilledText}
            onClearPrefilledText={() => setPrefilledText('')}
            pageContext="curiosity_video"
            curiosityContentId={contentId ? parseInt(contentId) : null}
            subjectId={content?.chapter_detail?.subject_id || selectedSubjectId || null}
            chapterId={content?.chapter_detail?.chapter_id || selectedChapterId || null}
            isMobileSplit={true}
          />
        </div>
      )}

      {/* Floating Budlee AI Mascot - Bottom Right Corner - Hidden when chat panel is open - Responsive Size */}
      {!showChatPanel && (
        <BudleeAIIcon
          size="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20"
          iconSize="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10"
          position="fixed bottom-20 sm:bottom-7 right-4 sm:right-6 lg:bottom-8 lg:right-8"
          onClick={handleAskBudleeAI}
          showBadge={true}
        />
      )}
      {renderAnimations()}
    </div>
  );
}

